import type {TFunction} from 'i18next';
import {VelaScene, type VelaContext} from '../engine/types';

export type VelaHeaderCopy = {
  name: string;
  status: string;
  intro: string;
};

const IV_SCENES = new Set<VelaScene>([
  VelaScene.IVStart,
  VelaScene.IVDocumentSelection,
  VelaScene.IVFront,
  VelaScene.IVBack,
  VelaScene.IVSelfie,
  VelaScene.IVContacts,
]);

function getIntroKey({
  scene,
  keysState,
  blockingKeyCondition,
  isDataPrefilled,
  cart,
}: VelaContext): string {
  if (scene === VelaScene.IVUnavailable) return 'intro.verification_unavailable';
  if (IV_SCENES.has(scene)) return 'intro.verification';
  if (scene === VelaScene.RemoteAccess) {
    if (keysState !== 'gated') return 'intro.remote_access';
    return blockingKeyCondition
      ? 'intro.remote_access_gated'
      : 'intro.remote_access_waiting_on_time';
  }
  if (scene === VelaScene.Taxes) return 'intro.taxes';
  if (scene === VelaScene.Deposit) return 'intro.deposit';
  if (scene === VelaScene.Payments) return 'intro.payments';
  if (scene === VelaScene.OrderHistory)
    return cart.hasReceipts === false
      ? 'intro.order_history_empty'
      : 'intro.order_history';
  if (scene === VelaScene.GuestRegistering) return 'intro.guest_registering';
  if (scene === VelaScene.GuestRegistrationFailed)
    return 'intro.guest_registration_failed';
  if (scene === VelaScene.Upselling) return 'intro.upselling';
  if (scene === VelaScene.UpsellingOffer) return 'intro.upselling_offer';
  if (scene === VelaScene.UpsellingRequests) return 'intro.upselling_requests';
  if (scene === VelaScene.Chat) return 'intro.chat';
  if (scene === VelaScene.ChatVerify) return 'intro.chat_verify';
  if (scene === VelaScene.ChatVerifyError) return 'intro.chat_verify_error';
  if (scene === VelaScene.ChatDisabled) return 'intro.chat_disabled';
  if (scene === VelaScene.ChatNotRegistered) return 'intro.chat_not_registered';
  if (scene === VelaScene.Esim) return 'intro.esim';
  if (scene === VelaScene.Checkout) return 'intro.checkout';
  if (scene === VelaScene.CheckoutRate) return 'intro.checkout_rate';
  if (scene === VelaScene.GuidebookDetail) return 'intro.guidebooks';
  if (scene === VelaScene.TravelGuide) return 'intro.travel_guide';
  if (isDataPrefilled) return 'intro.guest_form_prefilled';
  return 'intro.guest_form';
}

function getIntro(t: TFunction, ctx: VelaContext): string {
  if (ctx.scene === VelaScene.GuestRegistered) {
    return ctx.registeredFirstName
      ? t('intro.guest_registered', {name: ctx.registeredFirstName})
      : t('intro.guest_registered_anonymous');
  }
  if (ctx.scene !== VelaScene.GuestRegistration) return t(getIntroKey(ctx));
  if (ctx.pendingGuests > 0) {
    return t('intro.guest_registration_questions_pending', {count: ctx.pendingGuests});
  }
  return t('intro.guest_registration_questions_done');
}

export function getVelaHeader(t: TFunction, ctx: VelaContext): VelaHeaderCopy {
  return {
    name: t('name'),
    status: t('status_here_to_help'),
    intro: getIntro(t, ctx),
  };
}

type VelaProgressCopy = {
  title: string;
  intro: string;
};

export type VelaProgressVariant = 'lastStep' | 'inProgress' | 'skipped';

const PROGRESS_INTRO_KEY: Record<VelaProgressVariant, string> = {
  lastStep: 'progress.intro.last_step',
  inProgress: 'progress.intro.in_progress',
  skipped: 'progress.intro.skipped',
};

export function getVelaProgressCopy(
  t: TFunction,
  variant: VelaProgressVariant,
): VelaProgressCopy {
  return {
    title: t('progress.title'),
    intro: t(PROGRESS_INTRO_KEY[variant]),
  };
}
